import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getTokenClassName, highlightText, INDENT_SIZE } from './lib';
import { JsonNode } from './types';

/**
 * Пропсы рекурсивного рендерера узла JSON-дерева.
 */
type Props = {
  /** Узел дерева для отрисовки */
  node: JsonNode;
  /** Множество свернутых путей */
  collapsedPaths: Set<string>;
  /** Колбэк переключения сворачивания узла по пути */
  onToggle: (path: string) => void;
  /** Поисковый запрос для подсветки совпадений */
  searchQuery: string;
  /** Является ли узел последним в родительском контейнере (для запятой) */
  isLast: boolean;
};

/**
 * Рекурсивно рендерит узел JSON-дерева (объект или массив) в формате pretty-printed JSON
 * с возможностью сворачивания и подсветкой совпадений поиска.
 */
export const JsonNodeRenderer: FC<Props> = React.memo(({ node, collapsedPaths, onToggle, searchQuery, isLast }) => {
  const { t } = useTranslation('dialogs');
  const isCollapsed = collapsedPaths.has(node.path);
  const indent = ' '.repeat(node.depth * INDENT_SIZE);
  const childIndent = ' '.repeat((node.depth + 1) * INDENT_SIZE);
  const openBracket = node.type === 'object' ? '{' : '[';
  const closeBracket = node.type === 'object' ? '}' : ']';
  const comma = isLast ? '' : ',';
  const itemCount = node.items.length;

  const handleToggle = useCallback(() => {
    onToggle(node.path);
  }, [onToggle, node.path]);

  // Пустой объект/массив — отрисовываем компактно
  if (node.items.length === 0) {
    return (
      <>
        {node.key !== undefined && (
          <>
            <span className={getTokenClassName('key')}>{highlightText(JSON.stringify(node.key), searchQuery)}</span>
            <span className="text-muted-foreground">{': '}</span>
          </>
        )}
        <span className="text-muted-foreground">{`${openBracket}${closeBracket}`}</span>
        {comma && <span className="text-muted-foreground">{comma}</span>}
        {'\n'}
      </>
    );
  }

  if (isCollapsed) {
    const summaryKey = node.type === 'object' ? 'rawDataPropertiesCount' : 'rawDataItemsCount';
    return (
      <>
        {node.key !== undefined && (
          <>
            <span className={getTokenClassName('key')}>{highlightText(JSON.stringify(node.key), searchQuery)}</span>
            <span className="text-muted-foreground">{': '}</span>
          </>
        )}
        <span
          role="button"
          tabIndex={-1}
          className="text-muted-foreground cursor-pointer hover:text-foreground"
          onClick={handleToggle}
        >
          {openBracket}
        </span>
        <span
          role="button"
          tabIndex={-1}
          className="text-muted-foreground/60 cursor-pointer hover:text-foreground text-xs italic"
          onClick={handleToggle}
        >
          {` ${t(summaryKey, { count: itemCount })} `}
        </span>
        <span
          role="button"
          tabIndex={-1}
          className="text-muted-foreground cursor-pointer hover:text-foreground"
          onClick={handleToggle}
        >
          {closeBracket}
        </span>
        {comma && <span className="text-muted-foreground">{comma}</span>}
        {'\n'}
      </>
    );
  }

  return (
    <>
      {node.key !== undefined && (
        <>
          <span className={getTokenClassName('key')}>{highlightText(JSON.stringify(node.key), searchQuery)}</span>
          <span className="text-muted-foreground">{': '}</span>
        </>
      )}
      <span
        role="button"
        tabIndex={-1}
        className="text-muted-foreground cursor-pointer hover:text-foreground"
        onClick={handleToggle}
      >
        {openBracket}
      </span>
      {'\n'}

      {node.items.map((item, index) => {
        if (item.kind === 'leaf') {
          const leafComma = item.isLast ? '' : ',';
          return (
            <React.Fragment key={`${node.path}-leaf-${index}`}>
              {childIndent}
              {item.key !== undefined && (
                <>
                  <span className={getTokenClassName('key')}>
                    {highlightText(JSON.stringify(item.key), searchQuery)}
                  </span>
                  <span className="text-muted-foreground">{': '}</span>
                </>
              )}
              <span className={getTokenClassName(item.tokenType)}>{highlightText(item.value, searchQuery)}</span>
              {leafComma && <span className="text-muted-foreground">{leafComma}</span>}
              {'\n'}
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={`${node.path}-node-${index}`}>
            {childIndent}
            <JsonNodeRenderer
              node={item.node}
              collapsedPaths={collapsedPaths}
              onToggle={onToggle}
              searchQuery={searchQuery}
              isLast={item.isLast}
            />
          </React.Fragment>
        );
      })}

      {indent}
      <span
        role="button"
        tabIndex={-1}
        className="text-muted-foreground cursor-pointer hover:text-foreground"
        onClick={handleToggle}
      >
        {closeBracket}
      </span>
      {comma && <span className="text-muted-foreground">{comma}</span>}
      {'\n'}
    </>
  );
});
